import { PublicClientApplication, Configuration } from '@azure/msal-browser';
import { User, UserRole } from '../types/auth';

export interface IAuthService {
  login(email?: string): Promise<void>;
  logout(): Promise<void>;
  getAccessToken(): Promise<string | null>;
  isAuthenticated(): Promise<boolean>;
  getCurrentUser(): Promise<User | null>;
}

export class AuthService implements IAuthService {
  private msalInstance: PublicClientApplication;
  private initialized: boolean = false;

  constructor(msalInstance: PublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      console.log('🔍 Initializing MSAL...');
      await this.msalInstance.initialize();
      console.log('✅ MSAL initialized successfully');
      
      // Handle redirect response if any
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        console.log('✅ Redirect response handled, setting active account');
        this.msalInstance.setActiveAccount(response.account);
      } else {
        // Check if there are any accounts in the cache
        const accounts = this.msalInstance.getAllAccounts();
        console.log('🔍 Found accounts in cache:', accounts.length);
        if (accounts.length > 0) {
          console.log('✅ Setting active account from cache');
          this.msalInstance.setActiveAccount(accounts[0]);
        }
      }
      
      this.initialized = true;
    }
  }

  async login(email?: string): Promise<void> {
    try {
      await this.ensureInitialized();
      console.log('🔍 Starting MSAL login...');
      const response = await this.msalInstance.loginPopup({
        scopes: ['User.Read', 'GroupMember.Read.All'],
        loginHint: email
      });
      console.log('✅ MSAL login successful, setting active account...');
      
      // Set the active account after successful login
      this.msalInstance.setActiveAccount(response.account);
      console.log('✅ Active account set:', response.account?.name);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.ensureInitialized();
      await this.msalInstance.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }

  async getAccessToken(): Promise<string | null> {
    try {
      await this.ensureInitialized();
      const account = this.msalInstance.getActiveAccount();
      console.log('🔍 Getting access token - Active account:', account ? account.name : 'None');
      
      if (!account) {
        console.log('❌ No active account found');
        return null;
      }

      console.log('🔍 Acquiring token silently...');
      try {
        const response = await this.msalInstance.acquireTokenSilent({
          scopes: ['User.Read', 'GroupMember.Read.All'],
          account: account
        });

        console.log('✅ Token acquired successfully');
        return response.accessToken;
      } catch (silentError: any) {
        console.log('⚠️ Silent token acquisition failed, trying interactive login...');
        
        // If silent acquisition fails, try interactive login
        if (silentError.name === 'InteractionRequiredAuthError' || 
            silentError.errorCode === 'interaction_required' ||
            silentError.errorCode === 'consent_required') {
          
          console.log('🔄 Starting interactive login for token...');
          const response = await this.msalInstance.acquireTokenPopup({
            scopes: ['User.Read', 'GroupMember.Read.All'],
            account: account
          });
          
          console.log('✅ Interactive token acquisition successful');
          return response.accessToken;
        }
        
        throw silentError;
      }
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await this.ensureInitialized();
      const account = this.msalInstance.getActiveAccount();
      console.log('🔍 Checking authentication - Active account:', account ? account.name : 'None');
      
      if (!account) {
        // Check if there are any accounts in the cache
        const accounts = this.msalInstance.getAllAccounts();
        console.log('🔍 Found accounts in cache:', accounts.length);
        
        if (accounts.length > 0) {
          console.log('✅ Found cached account, setting as active:', accounts[0].name);
          this.msalInstance.setActiveAccount(accounts[0]);
          return true;
        }
      }
      
      return !!account;
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const token = await this.getAccessToken();
      if (!token) {
        console.error('No access token available');
        return null;
      }

      console.log('🔍 Getting user profile...');
      // Get user profile
      const profileResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!profileResponse.ok) {
        console.error('Failed to get user profile:', profileResponse.status, profileResponse.statusText);
        throw new Error('Failed to get user profile');
      }

      const profile = await profileResponse.json();
      console.log('✅ User profile retrieved:', profile.displayName);

      console.log('🔍 Getting user groups...');
      // Get user groups
      const groupsResponse = await fetch('https://graph.microsoft.com/v1.0/me/memberOf', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      let groups: string[] = [];
      let role: UserRole = 'volunteer'; // Default role

      if (!groupsResponse.ok) {
        console.error('Failed to get user groups:', groupsResponse.status, groupsResponse.statusText);
        console.log('⚠️ Using default role: volunteer');
      } else {
        const groupsData = await groupsResponse.json();
        groups = groupsData.value.map((group: any) => group.displayName);
        console.log('✅ User groups retrieved:', groups);
        
        // Determine role based on groups
        role = this.determineUserRole(groups);
        console.log('🎯 Determined role:', role);
      }

      const user: User = {
        id: profile.id,
        name: profile.displayName,
        email: profile.mail || profile.userPrincipalName,
        role,
        groups
      };

      console.log('👤 Final user object:', user);
      return user;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  private determineUserRole(groups: string[]): UserRole {
    console.log('🔍 Determining role from groups:', groups);
    
    // Filter out null values and create a clean array
    const cleanGroups = groups.filter(group => group !== null && group !== undefined);
    console.log('🔍 Clean groups:', cleanGroups);
    
    // Check for admin role first (highest priority)
    if (cleanGroups.some(group => group.includes('Admin'))) {
      console.log('✅ Found Admin group');
      return 'admin';
    }

    // Check for executive role
    if (cleanGroups.some(group => group.includes('Executive Board'))) {
      console.log('✅ Found Executive Board group');
      return 'executive';
    }

    // Check for manager role
    if (cleanGroups.some(group => group.includes('Managers'))) {
      console.log('✅ Found Managers group');
      return 'manager';
    }

    // Check for scheduling team
    if (cleanGroups.some(group => group.includes('Scheduling'))) {
      console.log('✅ Found Scheduling group');
      return 'scheduler';
    }

    // Check for onboarding/compliance
    if (cleanGroups.some(group => group.includes('Onboarding/Compliance'))) {
      console.log('✅ Found Onboarding/Compliance group');
      return 'onboarding';
    }

    // TEMPORARY: For testing, assign admin role if no groups found
    if (cleanGroups.length === 0) {
      console.log('⚠️ No groups found, temporarily assigning admin role for testing');
      return 'admin';
    }

    console.log('❌ No matching groups found, defaulting to volunteer');
    return 'volunteer';
  }
}

// Global instance
let authServiceInstance: AuthService | null = null;

const getRedirectUri = () => {
  // Use the current window location to automatically detect the port
  return window.location.origin;
};

const getClientId = () => {
  // In production, you can set this via Azure Static Web Apps environment variables
  return process.env.REACT_APP_CLIENT_ID || '300d5411-9056-4d1b-ae47-02fec49703cc';
};

const getTenantId = () => {
  // In production, you can set this via Azure Static Web Apps environment variables
  return process.env.REACT_APP_TENANT_ID || '55b468a7-1313-47f1-a25f-a6f05837196c';
};

export const getAuthService = (msalInstance?: PublicClientApplication): AuthService => {
  if (!authServiceInstance) {
    if (!msalInstance) {
      // Create a default MSAL instance if none provided
      const config: Configuration = {
        auth: {
          clientId: getClientId(),
          authority: `https://login.microsoftonline.com/${getTenantId()}`,
          redirectUri: getRedirectUri(),
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        }
      };
      msalInstance = new PublicClientApplication(config);
    }
    
    authServiceInstance = new AuthService(msalInstance);
  }
  
  return authServiceInstance;
};